import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  INCLUDE_EXCLUDE_BORDER,
  INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
} from "../IncludeExcludeFilter";
import {
  FilterState,
  IncludeExcludeFilterCategory,
} from "../reducers/includeExcludeFilterReducer";

export const FilterListOptions = (
  {
    validCategories,
    selectedCategory,
    setSelectedCategory,
    filterSelectionState,
    showSelectionCounts = false,
  }: {
    validCategories: IncludeExcludeFilterCategory[];
    selectedCategory: string;
    setSelectedCategory: (categoryId: string) => void;
    filterSelectionState: FilterState;
    showSelectionCounts?: boolean;
  }
) => {
  const theme = useTheme();

  const getSelectedCount = (category: IncludeExcludeFilterCategory): number => {
    const categoryIds = new Set<string>([category.id]);
    const visit = (options: typeof category.options) => {
      options.forEach((option) => {
        categoryIds.add(option.categoryId);
        if (option.subItems?.length) visit(option.subItems);
      });
    };
    visit(category.options);

    // A date-range category persists its selection as a single [start, end]
    // ISO-string pair (see handleSetDateRange), so a populated range counts as
    // one selection, not two, and a cleared range is stored as []. Checkbox and
    // date-operator categories store a flat list where each entry is its own
    // selection. The stored element type is always string, so the category
    // metadata -- not the element type -- is what distinguishes a date range.
    const isDateRangeCategory = category.type === "date-range";
    const countSelection = (
      selection: (typeof filterSelectionState.filters.include)[string] | undefined,
    ): number => {
      if (!selection || selection.length === 0) return 0;
      if (isDateRangeCategory) {
        return selection.some((entry) => entry != null) ? 1 : 0;
      }
      return selection.length;
    };

    return [...categoryIds].reduce(
      (count, categoryId) =>
        count +
        countSelection(filterSelectionState.filters.include[categoryId]) +
        countSelection(filterSelectionState.filters.exclude[categoryId]),
      0,
    );
  };

  return (
    <Box width={200} borderRight={INCLUDE_EXCLUDE_BORDER} overflow="auto">
        <Typography
          variant="subtitle2"
          fontWeight="medium"
          color="text.secondary"
          sx={{
            padding: 2,
            borderBottom: INCLUDE_EXCLUDE_BORDER,
            height: INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
          }}
        >
          Filters
        </Typography>
        <List dense sx={{ padding: 1 }}>
          {validCategories.map((cat) => {
            const selectedCount = showSelectionCounts ? getSelectedCount(cat) : 0;

            return (
              <ListItemButton
                key={cat.id}
                selected={selectedCategory === cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                sx={{
                  borderRadius: 1,
                  cursor: "pointer",
                  "&.Mui-selected": {
                    backgroundColor: "secondary.8p",
                  },
                }}
              >
                <ListItemText
                  secondary={
                    showSelectionCounts ? (
                      <Box
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Box component="span">{cat.label}</Box>
                        {selectedCount > 0 && (
                          <Box
                            component="span"
                            aria-label={`${selectedCount} selected`}
                            sx={{
                              alignItems: "center",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.12)"
                                  : "rgba(0,0,0,0.12)",
                              borderRadius: "50%",
                              color:
                                theme.palette.mode === "dark"
                                  ? theme.palette.common.white
                                  : theme.palette.common.black,
                              display: "inline-flex",
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: theme.spacing(2.5),
                              justifyContent: "center",
                              minWidth: theme.spacing(2.5),
                            }}
                          >
                            {selectedCount}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      cat.label
                    )
                  }
                  slotProps={{
                    secondary: {
                      color: "text.primary",
                      fontWeight: selectedCategory === cat.id ? "medium" : "normal",
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    );
};